import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setSelectedResource, selectSelectedResourceId } from '../slices/preferencesSlice';

// Routes where the resource selection is part of the view identity.
const RESOURCE_SCOPED_PATHS = ['/dashboard', '/forecasts', '/analytics', '/users/payments'];

export default function ResourceUrlSync() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const selectedId = useSelector(selectSelectedResourceId);
  const resources = useSelector((state) => state.user.resources);
  const seeded = useRef(false);

  const scoped = RESOURCE_SCOPED_PATHS.includes(location.pathname);

  // URL -> Redux, once, after the resource list is known. Reads the router
  // location: pages never write the `resource` param themselves, so there is
  // no staleness concern here (and tests use MemoryRouter, where
  // window.location is not wired up).
  useEffect(() => {
    if (seeded.current || !scoped || !resources || resources.length === 0) return;
    const param = new URLSearchParams(location.search).get('resource');
    const match = param && resources.find((r) => String(r.id) === String(param));
    if (match && String(match.id) !== String(selectedId)) {
      dispatch(setSelectedResource(match.id));
    }
    seeded.current = true;
  }, [scoped, resources, location.search, selectedId, dispatch]);

  // Redux -> URL (after seeding) so switches and direct visits get a shareable
  // URL. Reads window.location.search, NOT the render closure: this component
  // mounts in Layout, ABOVE the page, so its effects run after the page's URL
  // write-back in the same pass. history.replaceState is synchronous, so
  // window.location already includes params the page just wrote; a stale
  // closure here would silently drop them. (The app router is BrowserRouter,
  // so window.location always mirrors the router location.)
  useEffect(() => {
    if (!scoped || !seeded.current || selectedId == null) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('resource') !== String(selectedId)) {
      params.set('resource', String(selectedId));
      navigate({ search: `?${params.toString()}` }, { replace: true });
    }
  }, [scoped, selectedId, location.search, navigate]);

  return null;
}
